/**
 * Canonical API Client
 *
 * Central axios instance with:
 * - Automatic X-Idempotency-Key injection for mutations
 * - X-Correlation-Id capture for support/debugging
 * - Auth token handling
 * - Error handling with correlation context
 *
 * ALL API calls MUST go through this client.
 */

import axios from "axios";
import { idemKey } from "./idempotency";
import {
    deviceFingerprintHeaders,
    getDeviceFingerprint,
} from "../utils/deviceFingerprint";

// Kick off fingerprint computation as early as possible; subsequent calls
// reuse the cached value. Exposed on window so callers (e.g. auth forms that
// post without the shared interceptor) can await it.
let _fingerprintPromise = null;
const ensureFingerprint = () => {
    if (!_fingerprintPromise) {
        _fingerprintPromise = getDeviceFingerprint().catch(() => null);
    }
    return _fingerprintPromise;
};
if (typeof window !== "undefined") {
    window.__gvDeviceFp = ensureFingerprint;
}

// Base URL from environment
// Empty string = relative path (proxy will handle routing to backend)
// Set REACT_APP_BACKEND_URL in production to full URL
const BASE_URL = process.env.REACT_APP_BACKEND_URL || "";
const API_BASE = BASE_URL ? `${BASE_URL}/api` : "/api";

// Singleton store for last correlation ID
let lastCorrelationId = null;

export const getLastCorrelationId = () => lastCorrelationId;

// Pure token selector shared by the request interceptor and focused tests.
// Vendor endpoints deliberately ignore platform-admin and player sessions so
// a vendor-scoped API can never receive the wrong actor's bearer token.
export function selectApiAuthToken(urlValue = "", storage) {
    const read = (key) => storage?.getItem?.(key) || null;
    const isAdminEndpoint =
        urlValue.includes("/admin/") ||
        urlValue.startsWith("admin/") ||
        urlValue.includes("/superadmin/") ||
        urlValue.startsWith("superadmin/");
    const isVendorEndpoint =
        urlValue.includes("/vendor/") || urlValue.startsWith("vendor/");
    const isChatPushEndpoint =
        urlValue.includes("/chat/local/push/") ||
        urlValue.startsWith("chat/local/push/");

    let token;
    if (isAdminEndpoint) {
        token =
            read("adminSessionId") || read("vendor_token") || read("vendorToken");
    } else if (isVendorEndpoint) {
        token = read("vendor_token") || read("vendorToken");
    } else {
        token =
            read("sessionId") ||
            read("clubsClientToken") ||
            read("client_session_id") ||
            read("client_token") ||
            read("authToken") ||
            (isChatPushEndpoint
                ? read("vendor_token") || read("vendorToken")
                : null);
    }

    return { token, isAdminEndpoint, isVendorEndpoint };
}

// Create axios instance
const apiClient = axios.create({
    baseURL: API_BASE,
    timeout: 30000,
    withCredentials: false, // Disabled due to Cloudflare proxy returning * for Access-Control-Allow-Origin
    headers: {
        "Content-Type": "application/json",
    },
});

/**
 * Request Interceptor
 * - Attach auth token if available
 * - Inject X-Idempotency-Key for mutations (POST/PUT/PATCH/DELETE)
 */
apiClient.interceptors.request.use(
    async (config) => {
        // Attach device fingerprint on every request so the backend can
        // enforce multi-account caps and trace suspicious device reuse.
        try {
            await ensureFingerprint();
            Object.assign(config.headers, await deviceFingerprintHeaders());
        } catch {
            /* fingerprint is best-effort */
        }

        // Attach auth token if available
        // Use adminSessionId for admin endpoints, sessionId for regular endpoints
        const urlValue = config.url || "";
        const { token, isAdminEndpoint } = selectApiAuthToken(
            urlValue,
            localStorage,
        );

        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // Multi-tenant impersonation: if master admin has selected a vendor to
        // "view as", attach the header so the backend treats the request as a
        // synthetic vendor_admin. Only applied to admin endpoints.
        if (isAdminEndpoint) {
            const impersonateId = localStorage.getItem("impersonateVendorId");
            if (impersonateId) {
                config.headers["X-Impersonate-Vendor"] = impersonateId;
            }
        }

        // For mutations, ensure X-Idempotency-Key exists
        const isMutation = ["post", "put", "patch", "delete"].includes(
            config.method?.toLowerCase(),
        );
        if (isMutation && !config.headers["X-Idempotency-Key"]) {
            // Generate default idempotency key if not provided
            // Using URL + timestamp + random for uniqueness
            const url = config.url || "";
            const timestamp = Date.now();
            const random = Math.random().toString(36).substring(2, 9);
            const defaultKey = `ui:auto:${url.replace(
                /\//g,
                "_",
            )}:${timestamp}:${random}`;
            config.headers["X-Idempotency-Key"] = defaultKey;

            console.warn(
                `[apiClient] No idempotency key provided for ${config.method?.toUpperCase()} ${
                    config.url
                }. ` +
                    `Auto-generated: ${defaultKey}. ` +
                    `Consider using IdemKeys helper for deterministic keys.`,
            );
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    },
);

/**
 * Response Interceptor
 * - Capture X-Correlation-Id from response headers
 * - Attach correlation ID to errors for support
 */
apiClient.interceptors.response.use(
    (response) => {
        // Capture correlation ID
        const correlationId = response.headers["x-correlation-id"];
        if (correlationId) {
            lastCorrelationId = correlationId;
            // Optionally store in response for easy access
            response.correlationId = correlationId;
        }

        return response;
    },
    (error) => {
        // Capture correlation ID from error response
        if (error.response?.headers) {
            const correlationId = error.response.headers["x-correlation-id"];
            if (correlationId) {
                lastCorrelationId = correlationId;
                error.correlationId = correlationId;
            }
        }

        // Handle 401 - session expired/invalid: clear session and reload
        // Multi-tenant fix: only force-clear for /auth/me (canonical session probe).
        // Master-only admin endpoints return 401 for vendor sessions — don't
        // treat that as "session invalid", just reject the call.
        if (error.response?.status === 401) {
            const reqUrl = error.config?.url || "";
            const isSessionProbe = reqUrl.includes("/auth/me");
            // `/admin/me` is the admin console's session probe. It also accepts
            // vendor sessions, so a 401 here only proves the ADMIN session is
            // dead — and only when one is actually stored. Clear just the admin
            // keys; a coexisting vendor/client session must survive.
            const isAdminSessionProbe =
                reqUrl.includes("/admin/me") &&
                !!localStorage.getItem("adminSessionId");
            if (isAdminSessionProbe && !isSessionProbe) {
                localStorage.removeItem("adminSessionId");
                localStorage.removeItem("adminUser");
                localStorage.removeItem("adminId");
                if (!window.__sessionClearing) {
                    window.__sessionClearing = true;
                    window.location.href = "/superadmin/login";
                    return new Promise(() => {});
                }
            }
            if (isSessionProbe) {
                const hadSession =
                    !!localStorage.getItem("sessionId") ||
                    !!localStorage.getItem("adminSessionId");
                localStorage.removeItem("sessionId");
                localStorage.removeItem("user");
                localStorage.removeItem("adminSessionId");
                localStorage.removeItem("adminUser");
                if (hadSession && !window.__sessionClearing) {
                    window.__sessionClearing = true;
                    window.location.href = "/auth/login";
                    return new Promise(() => {});
                }
            }
        }

        // Enhance error with server message for cleaner toasts
        if (error.response?.data) {
            const serverMsg =
                error.response.data.message || error.response.data.detail;
            if (serverMsg) {
                error.serverMessage = serverMsg;
            }
        }

        // Enhance error message with correlation ID for support
        if (error.correlationId) {
            // Add to error response data if available
            if (
                error.response?.data &&
                typeof error.response.data === "object"
            ) {
                error.response.data.correlationId = error.correlationId;
            }
        }

        return Promise.reject(error);
    },
);

/**
 * Helper to create request with explicit idempotency key
 *
 * @param {string} method - HTTP method
 * @param {string} url - Request URL
 * @param {object} data - Request body
 * @param {string} idemKey - Idempotency key
 * @param {object} config - Additional axios config
 */
export const requestWithIdemKey = (method, url, data, idemKey, config = {}) => {
    return apiClient.request({
        method,
        url,
        data,
        ...config,
        headers: {
            ...config.headers,
            "X-Idempotency-Key": idemKey,
        },
    });
};

/**
 * Convenience methods with idempotency key support
 */
export const api = {
    // GET (no idempotency needed)
    get: (url, config) => apiClient.get(url, config),

    // POST with idempotency
    post: (url, data, idemKey, config = {}) => {
        if (!idemKey) {
            console.error("[apiClient] POST without idempotency key:", url);
        }
        return requestWithIdemKey("post", url, data, idemKey, config);
    },

    // PUT with idempotency
    put: (url, data, idemKey, config = {}) => {
        if (!idemKey) {
            console.error("[apiClient] PUT without idempotency key:", url);
        }
        return requestWithIdemKey("put", url, data, idemKey, config);
    },

    // PATCH with idempotency
    patch: (url, data, idemKey, config = {}) => {
        if (!idemKey) {
            console.error("[apiClient] PATCH without idempotency key:", url);
        }
        return requestWithIdemKey("patch", url, data, idemKey, config);
    },

    // DELETE with idempotency
    delete: (url, idemKey, config = {}) => {
        if (!idemKey) {
            console.error("[apiClient] DELETE without idempotency key:", url);
        }
        return requestWithIdemKey("delete", url, null, idemKey, config);
    },
};

// Export default axios instance for backward compatibility
export default apiClient;
